/**
 * Business Logic Constants for Lead Magnate Analytics
 * Centralized constants for call classifications, pricing, and business rules
 */

// ============================================================================
// LEAD CLASSIFICATIONS
// ============================================================================

// Lead R = waarborg-formule (recurring buiten 1-maand window).
// Lead D = cross-campaign duplicate-detect (zelfde from naar zelfde klant binnen 24u).
// Lead N / Lead C = AI labels (non-lead hard / twijfel).
// Allemaal default-billable: bij twijfel = factureren. Dennis kan handmatig downgraden
// naar Terugkerende beller / Waardeloos / Geen oplossing mogelijk als nodig.
export const LEAD_CLASSIFICATIONS = ['-', 'Verkoop', 'Lead', 'Lead X', 'Lead gemist', 'Lead N', 'Lead C', 'Lead R', 'Lead D'];
export const NON_LEAD_CLASSIFICATIONS = ['Terugkerende beller', 'Overig', 'Waardeloos', 'Geen oplossing mogelijk'];

// All classifications combined for validation
export const ALL_CLASSIFICATIONS = [...LEAD_CLASSIFICATIONS, ...NON_LEAD_CLASSIFICATIONS];

// Helper function to check if classification is a lead
// Default-allow logic (21 mei 2026): lead tenzij expliciet in NON_LEAD-lijst.
// Risico-asymmetrie: bij twijfel factureren = omzet behouden. Onbekende
// classificaties (bv. nieuwe Qooqie-categorie die wij nog niet kennen)
// worden default als lead behandeld.
export const isLeadClassification = (classification) => {
  return !NON_LEAD_CLASSIFICATIONS.includes(classification);
};

// Helper function to check if classification is a lead for AdminDashboard
// Special logic: recurring calls with '-' zijn non-leads (Qooqie auto-recurring
// filtert ze terecht), recurring met andere niet-NON_LEAD waarde = lead.
export const isLeadClassificationForAdmin = (classification, isRecurring) => {
  // Normalize isRecurring to boolean (handle string 'true'/'false')
  const isRecurringCall = isRecurring === true || isRecurring === 'true';

  // If it's a recurring call
  if (isRecurringCall) {
    // Recurring + '-' = Qooqie auto-recurring (Lead R formule schrijft expliciet
    // wanneer dit wel een lead is)
    if (classification === '-') {
      return false;
    }
    // Default-allow: alles wat niet expliciet non-lead is = lead
    return !NON_LEAD_CLASSIFICATIONS.includes(classification);
  }

  // For non-recurring calls: default-allow
  return !NON_LEAD_CLASSIFICATIONS.includes(classification);
};

// Helper function to check if classification is non-lead
export const isNonLeadClassification = (classification) => {
  return NON_LEAD_CLASSIFICATIONS.includes(classification);
};

// ============================================================================
// CLASSIFICATION EMOJIS
// ============================================================================

export const CLASSIFICATION_EMOJIS = {
  '-': '💰',
  'Verkoop': '🏆', 
  'Lead': '🎯',
  'Lead X': '⭐',
  'Lead gemist': '⏰',
  'Terugkerende beller': '🔄',
  'Overig': '❓',
  'Waardeloos': '❌',
  'Geen oplossing mogelijk': '🚫'
};

// ============================================================================
// NICHE DEFINITIONS
// ============================================================================

export const NICHE_EMOJIS = {
  'Ontstoppen': '🚰',
  'Loodgieter': '🔧',
  'Dakdekker': '🏠',
  'Schoorsteenveger (NL)': '🏭',
  'UK Projects': '🇬🇧',
  'USA Projects': '🇺🇸',
  'Niet Ingesteld': '❓',
  'Other': '❓'
};

// Niche keywords for identification
export const NICHE_KEYWORDS = {
  'Ontstoppen': [
    'ontstoppen', 'riool ontstoppen', 'ontstoppingsdienst', 'ontstoppingsbedrijf',
    'drain cleaning', 'drain service', 'afvoer', 'verstopping'
  ],
  'Loodgieter': [
    'loodgieter', 'mr loodgieter', 'sanitair', 'lekkage'
  ],
  'Dakdekker': [
    'dakdekker', 'dak reparatie', 'dakbedekking'
  ],
  'Schoorsteenveger (NL)': [
    'schoorsteenveger', 'schoorsteen', 'rookkanaal'
  ],
  'UK Projects': [
    'birmingham', 'plumber birmingham', 'roofer birmingham'
  ],
  'USA Projects': [
    'drroof', '.com', 'florida', 'usa'
  ],
  'Niet Ingesteld': [
    'not set', '', 'undefined'
  ]
};

// Revenue-generating niches (Dutch market only)
export const REVENUE_NICHES = ['Ontstoppen', 'Loodgieter', 'Dakdekker'];

// Non-revenue niches (international/test projects)
export const NON_REVENUE_NICHES = ['Schoorsteenveger (NL)', 'UK Projects', 'USA Projects', 'Niet Ingesteld', 'Other'];

// Helper function to check if niche generates revenue
export const isRevenueNiche = (niche) => {
  return REVENUE_NICHES.includes(niche);
};

// Bepaal niche uit campagne-naam (spiegelt calculateBilling.getNicheFromCampaign).
// Alleen revenue-niches worden herkend; alles anders → null (= geen billable niche).
// Voorkomt dat "VTH installatiebedrijf V0164"-achtige campagnes als lead tellen
// op de klant-dashboard terwijl de billing engine ze wél terecht weglaat.
export const getNicheFromCampaign = (campaign) => {
  if (!campaign) return null;
  const lower = campaign.toLowerCase();
  for (const niche of REVENUE_NICHES) {
    const keywords = NICHE_KEYWORDS[niche] || [];
    if (keywords.some(k => lower.includes(k))) return niche;
  }
  return null;
};

// ============================================================================
// DEFAULT PRICING
// ============================================================================

export const DEFAULT_PRICES = {
  'Ontstoppen': 32,
  'Loodgieter': 18,
  'Dakdekker': 42,
  'Other': 25
};

// ============================================================================
// DUTCH CITIES LIST
// ============================================================================

export const DUTCH_CITIES = [
  'Amsterdam', 'Rotterdam', 'Den Haag', 'Utrecht', 'Eindhoven', 'Tilburg', 
  'Groningen', 'Almere', 'Breda', 'Nijmegen', 'Enschede', 'Haarlem', 'Arnhem', 
  'Zaanstad', 'Amersfoort', 'Apeldoorn', 'Hoofddorp', 'Maastricht', 'Leiden', 
  'Dordrecht', 's-Hertogenbosch', 'Zoetermeer', 'Zwolle', 'Gouda', 'Delft', 
  'Alkmaar', 'Hoorn', 'Purmerend', 'Roosendaal', 'Helmond', 'Venlo', 
  'Leeuwarden', 'Emmen', 'Ede', 'Veenendaal', 'Nieuwegein', 'Hilversum', 
  'Hengelo', 'Amstelveen', 'Capelle aan den IJssel', 'Rijswijk', 
  'Leidschendam', 'Voorschoten'
];

// ============================================================================
// NICHE KEYWORDS FOR CAMPAIGN CLEANING
// ============================================================================

export const NICHE_PREFIXES = [
  'loodgieter', 'mr loodgieter', 'ontstoppen', 'riool ontstoppen', 
  'ontstoppingsdienst', 'ontstoppingsbedrijf', 'drain cleaning',
  'dakdekker', 'dak reparatie', 'dakbedekking', 'schoorsteenveger', 'schoorsteen'
];

// ============================================================================
// CUSTOMER CODE PATTERNS
// ============================================================================

// Regex pattern for customer codes (e.g., M023, A020a, R0182)
export const CUSTOMER_CODE_PATTERN = /([A-Z]\d{3,4}[A-Za-z]?)/;

// Special multi-code clients
export const MULTI_CODE_CLIENTS = {
  'M023': {
    allCodes: ['M023D', 'M023'],
    clientName: 'MMA'
  }
  // Add more multi-code clients here as needed
};

// ============================================================================
// PERIOD LABELS
// ============================================================================

export const PERIOD_LABELS = {
  'today': 'VANDAAG',
  'yesterday': 'GISTEREN',
  'thisWeek': 'DEZE WEEK',
  'lastWeek': 'VORIGE WEEK',
  'thisMonth': 'DEZE MAAND',
  'lastMonth': 'VORIGE MAAND',
  'last7days': 'LAATSTE 7 DAGEN',
  'last30days': 'LAATSTE 30 DAGEN',
  'custom': 'AANGEPASTE PERIODE'
};

// ============================================================================
// CHART COLORS
// ============================================================================

export const CHART_COLORS = ['#D02E17', '#31B9CE', '#6B7280', '#9CA3AF'];

// Brand colors
export const BRAND_COLORS = {
  PRIMARY_RED: '#D02E17',
  COOKIES_CYAN: '#31B9CE',
  GRAY_600: '#6B7280',
  GRAY_400: '#9CA3AF'
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

export const validateClassification = (classification) => {
  return ALL_CLASSIFICATIONS.includes(classification);
};

export const validateNiche = (niche) => {
  return Object.keys(NICHE_EMOJIS).includes(niche);
};

export const validateCustomerCode = (code) => {
  if (!code) return false;
  return CUSTOMER_CODE_PATTERN.test(code);
};

// ============================================================================
// BUSINESS RULES
// ============================================================================

export const BUSINESS_RULES = {
  // Only lead classifications generate revenue
  REVENUE_CALCULATION: 'Only calls with lead classifications count toward revenue',
  
  // Only Dutch niches generate revenue
  REVENUE_NICHES_ONLY: 'Only Dutch market niches (Ontstoppen, Loodgieter, Dakdekker) generate revenue',
  
  // Campaign structure
  CAMPAIGN_STRUCTURE: '[COOKIES: ][Niche] [Plaats] [Klantcode]',
  
  // Timezone
  TIMEZONE: 'Europe/Amsterdam',
  
  // Currency
  CURRENCY: 'EUR',
  
  // Date format
  DATE_FORMAT: 'nl-NL'
};

// ============================================================================
// EXPORT ALL
// ============================================================================

const businessLogic = {
  LEAD_CLASSIFICATIONS,
  NON_LEAD_CLASSIFICATIONS,
  ALL_CLASSIFICATIONS,
  CLASSIFICATION_EMOJIS,
  NICHE_EMOJIS,
  NICHE_KEYWORDS,
  REVENUE_NICHES,
  NON_REVENUE_NICHES,
  DEFAULT_PRICES,
  DUTCH_CITIES,
  NICHE_PREFIXES,
  CUSTOMER_CODE_PATTERN,
  MULTI_CODE_CLIENTS,
  PERIOD_LABELS,
  CHART_COLORS,
  BRAND_COLORS,
  BUSINESS_RULES,
  isLeadClassification,
  isNonLeadClassification,
  isRevenueNiche,
  validateClassification,
  validateNiche,
  validateCustomerCode
};

export default businessLogic;
